import ko from 'knockout';
import { debounce } from 'lodash-es';
import { customFetch as fetch, CustomResponse, CustomError } from '../../fetch';
import { lookupAddress } from '../../postalCodeApi';
import { KvkSearchResultModel, searchCompanies } from '../../custom/kvk';
import { resolveCdnUrl } from "../../cdn-url-resolver";

declare var model: any;

class Issuer {
    id: string;
    name: string;
    method: string;
}

interface IForm {
    submit(): Promise<CustomResponse>;
}

class RegistrationModel {
    initialized: KnockoutObservable<boolean>;

    currentStep: KnockoutObservable<number>;
    nextStepLoading: KnockoutObservable<boolean>;

    searchCompany: KnockoutObservable<SearchCompanyModel>;
    profile: KnockoutObservable<ProfileDetailsModel>;
    verification: KnockoutObservable<VerificationModel>;

    validationErrors: KnockoutObservableArray<CustomError>;

    showNextStep: KnockoutComputed<boolean>;

    isVerified: KnockoutComputed<boolean>;

    constructor(model: any = {}) {
        this.currentStep = ko.observable<number>(model.step || 1);
        this.nextStepLoading = ko.observable<boolean>(false);
        this.validationErrors = ko.observableArray<CustomError>();
        this.initialized = ko.observable<boolean>(false);

        this.searchCompany = ko.observable<SearchCompanyModel>(new SearchCompanyModel(model));
        this.profile = ko.observable<ProfileDetailsModel>(new ProfileDetailsModel(model, this));
        this.verification = ko.observable<VerificationModel>(new VerificationModel(model));

        this.showNextStep = ko.pureComputed(() => {
            if (this.currentStep() === 1) {
                const { selectedCompany, companyNotFoundMessage } = this.searchCompany();
                return selectedCompany() != null || companyNotFoundMessage() != null;
            }

            return this.currentStep() > 1 && this.currentStep() < 3;
        });

        this.isVerified = ko.pureComputed(() => {
            const verification = this.verification();
            return this.currentStep() === 3
                && verification.emailVerified()
                && verification.adminVerified();
        });

        this.initialized(true);
    }

    public previousStep() {
        this.currentStep(this.currentStep() - 1);
    }

    public async nextStep() {
        if (this.currentStep() === 1) {
            const selectedCompany = this.searchCompany().selectedCompany();
            if (selectedCompany) {
                const profile = this.profile();
                profile.address(selectedCompany.address());
                profile.city(selectedCompany.city());
                profile.companyName(selectedCompany.companyName());
                profile.kvkNumber(selectedCompany.kvkNumber());
            }
        }

        let currentForm = this.currentForm();
        if (currentForm) {
            this.nextStepLoading(true);
            const result = await currentForm.submit();
            this.nextStepLoading(false);
            if (result.errors) {
                this.validationErrors(result.errors);

                if (result.errors.length > 0) {
                    const timelineElement = document.getElementById('timeline')!;
                    timelineElement.scrollIntoView();
                    return;
                }
            } else {
                if (currentForm instanceof ProfileDetailsModel) {
                    //Reload after registration submission for Identity to reload IsAuthenticated status
                    window.location.reload();
                    return;
                }
            }
        }

        this.validationErrors([]);
        let nextStep = this.currentStep() + 1;
        this.currentStep(nextStep);
    }

    public toStep(step: number) {
        if (this.currentStep() > step) {
            this.currentStep(step);
        }
    }

    public async finishRegistration() {
        const response = await fetch('/Account/FinishRegistration', {
            method: 'post'
        });

        if (response.ok) {
            window.location.href = '/';
        }
    }

    private currentForm() {
        switch (this.currentStep()) {
            case 2:
                return this.profile();
        }

        return null;
    }
}

class SearchCompanyModel {
    errors: KnockoutObservableArray<CustomError>;
    searchString: KnockoutObservable<string>;
    searchResults: KnockoutObservableArray<KvkSearchResultModel>;
    selectedCompany: KnockoutObservable<KvkSearchResultModel | undefined>;
    searching: KnockoutObservable<boolean>;

    searchResultsText: KnockoutComputed<string>;
    showSearchResults: KnockoutComputed<boolean>;
    minSearchLengthReached: KnockoutComputed<boolean>;
    companyNotFoundMessage: KnockoutComputed<string | undefined>;

    constructor(model: any = {}) {
        this.errors = ko.observableArray<CustomError>([]);
        this.searchString = ko.observable<string>(model.kvKNumber || '');
        this.searchString.subscribe(this.searchCompanies);
        this.searchResults = ko.observableArray<KvkSearchResultModel>([]);
        this.selectedCompany = ko.observable<KvkSearchResultModel>();
        this.searching = ko.observable<boolean>(false);

        this.minSearchLengthReached = ko.pureComputed(() => this.searchString().length >= 3);
        this.companyNotFoundMessage = ko.pureComputed(() => this.errors().find(error => error.key === "CompanyNotFound")?.message);

        this.searchResultsText = ko.pureComputed(() => {
            if (!this.minSearchLengthReached()) {
                return 'Voer minimaal 3 karakters in om te zoeken';
            }

            const companyNotFoundMessage = this.companyNotFoundMessage()
            if (this.companyNotFoundMessage()) {
                return `${companyNotFoundMessage}<br><br>Je kunt alvast naar de volgende stap gaan om alsnog handmatig je bedrijfsgegevens in te vullen.`;
            }

            const count = this.searchResults().length;
            if (!this.searching() && count > 0) {
                return `${count} ${count === 1 ? 'zoekresultaat' : 'zoekresultaten'}`;
            }

            return 'Bezig met zoeken...';
        });

        this.showSearchResults = ko.pureComputed(() => this.minSearchLengthReached() && !this.searching() && this.searchResults().length > 0);
    }

    searchCompanies = debounce(async (searchString: string) => {
        if (searchString.length < 3) {
            this.errors([]);
            this.searchResults([]);
            this.selectedCompany(undefined);
            return;
        }

        this.selectedCompany(undefined);
        this.searching(true);
        this.errors([]);

        const response = await searchCompanies(searchString);

        if (response.ok) {
            const data = response.data.results && Array.isArray(response.data.results)
                ? response.data.results.filter((i: any) => !!i.establishmentNumber)
                : [];

            this.searchResults(data.map((d: any) => new KvkSearchResultModel(d)));
        } else if (response.errors && response.errors.length > 0) {
            this.searchResults([]);
            this.errors(response.errors);
        }

        this.searching(false);
    }, 500);
}

class ProfileDetailsModel implements IForm {
    parent: KnockoutObservable<RegistrationModel>;

    userId: KnockoutObservable<string>;

    companyAvatar: KnockoutObservable<any>;
    companyAvatarHash: KnockoutObservable<string>;
    companyAvatarUrl: KnockoutObservable<string>;
    companyAvatarSrc: KnockoutComputed<any>;

    username: KnockoutObservable<string>;
    password: KnockoutObservable<string>;
    confirmPassword: KnockoutObservable<string>;

    companyName: KnockoutObservable<string>;
    kvkNumber: KnockoutObservable<string>;
    companyPhoneNumber: KnockoutObservable<string>;
    companySite: KnockoutObservable<string>;
    isCompanyEmailSameAsUsername: KnockoutObservable<boolean>;
    companyEmail: KnockoutObservable<string>;
    isCompanyInvoiceEmailSameAsUsername: KnockoutObservable<boolean>;
    companyInvoiceEmail: KnockoutObservable<string>;

    functionName: KnockoutObservable<string>;
    firstName: KnockoutObservable<string>;
    lastName: KnockoutObservable<string>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;
    phoneNumber: KnockoutObservable<string>;
    vatNumber: KnockoutObservable<string>;

    acceptedTermsAndConditions: KnockoutObservable<boolean>;

    constructor(model: any = {}, parent: RegistrationModel) {
        this.parent = ko.observable<RegistrationModel>(parent);

        this.companyAvatar = ko.observable<any>({
            dataURL: ko.observable<any>(),
            file: ko.observable<any>()
        }).extend({
            errorHandler: {
                source: parent.validationErrors, id: 'CompanyAvatar'
            }
        });

        this.companyAvatarHash = ko.observable(model.avatarHash || '');
        this.companyAvatarUrl = ko.observable(model.avatarUrl);

        this.companyAvatar().dataURL.subscribe(() => {
            this.companyAvatarHash('');
            this.companyAvatarUrl('');
        });

        if (this.companyAvatarUrl() || this.companyAvatarHash()) {
            this.companyAvatar().file = ko.observable({ name: model.avatarFileName });
        }

        this.companyAvatarSrc = ko.pureComputed(() => {
            if (this.companyAvatar().dataURL()) {
                console.log(this.companyAvatar().dataURL());
                return this.companyAvatar().dataURL();
            }

            if (this.companyAvatarHash()) {
                return resolveCdnUrl(this.companyAvatarHash());
            }

            if (this.companyAvatarUrl()) {
                return this.companyAvatarUrl();
            }

            return '';
        });

        this.userId = ko.observable<string>(model.userId || '');

        this.username = ko.observable<string>(model.userName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'UserName' }
            });
        this.password = ko.observable<string>('')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'Password' }
            });
        this.confirmPassword = ko.observable<string>('')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'ConfirmPassword' }
            });

        this.companyName = ko.observable<string>(model.companyName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'CompanyName' }
            });
        this.kvkNumber = ko.observable<string>(model.kvKNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'KvKNumber' }
            });

        this.functionName = ko.observable<string>(model.functionName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'FunctionName' }
            });

        this.firstName = ko.observable<string>(model.firstName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'FirstName' }
            });
        this.lastName = ko.observable<string>(model.lastName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'LastName' }
            });
        this.address = ko.observable<string>(model.address || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'Address' }
            });
        this.postalCode = ko.observable<string>(model.postalCode || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'PostalCode' }
            });
        this.houseNumber = ko.observable<string>(model.houseNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'HouseNumber' }
            });
        this.city = ko.observable<string>(model.city || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'City' }
            });
        this.phoneNumber = ko.observable<string>(model.phoneNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'PhoneNumber' }
            });

        this.acceptedTermsAndConditions = ko.observable<boolean>(false)
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'AcceptedTermsConditions' }
            });

        this.companyPhoneNumber = ko.observable<string>(model.companyPhoneNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'CompanyPhoneNumber' }
            });

        this.companySite = ko.observable<string>(model.companySite || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'CompanySite' }
            });

        this.isCompanyEmailSameAsUsername = ko.observable<boolean>(true);

        this.companyEmail = ko.observable<string>(model.companyEmail || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'CompanyEmail' }
            });

        this.isCompanyInvoiceEmailSameAsUsername = ko.observable<boolean>(true);

        this.companyInvoiceEmail = ko.observable<string>(model.companyInvoiceEmail || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'CompanyInvoiceEmail' }
            });

        this.vatNumber = ko.observable<string>(model.vatNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'VatNumber' }
            });
    }

    clearCompanyAvatar(): void {
        if (typeof (this.companyAvatar().clear) === 'function') {
            this.companyAvatar().clear();
        }
    }

    async submit(): Promise<CustomResponse> {
        let fd = new FormData();
        fd.append('companyAvatar', this.companyAvatar().file());
        fd.append('companyAvatarHash', this.companyAvatarHash());
        fd.append('userName', this.username());
        fd.append('password', this.password());
        fd.append('confirmPassword', this.confirmPassword());

        fd.append('companyName', this.companyName());
        fd.append('kvkNumber', this.kvkNumber());
        fd.append('companyPhoneNumber', this.companyPhoneNumber());
        fd.append('companySite', this.companySite());
        const companyEmail = this.isCompanyEmailSameAsUsername()
            ? this.username()
            : this.companyEmail();
        fd.append('companyEmail', companyEmail);
        const companyInvoiceEmail = this.isCompanyInvoiceEmailSameAsUsername()
            ? this.username()
            : this.companyInvoiceEmail();
        fd.append('companyInvoiceEmail', companyInvoiceEmail);

        fd.append('functionName', this.functionName());
        fd.append('firstName', this.firstName());
        fd.append('lastName', this.lastName());
        fd.append('address', this.address());
        fd.append('houseNumber', this.houseNumber());
        fd.append('postalCode', this.postalCode());
        fd.append('city', this.city());
        fd.append('phoneNumber', this.phoneNumber());
        fd.append('vatNumber', this.vatNumber());

        fd.append('acceptedTermsConditions', this.acceptedTermsAndConditions().toString());

        const url = this.userId()
            ? '/Account/EditRegistrationEmployer'
            : '/Account/RegisterEmployer';

        const response = await fetch(url, {
            body: fd,
            method: 'post'
        });

        if (response.errors && response.errors.length === 0) {
            this.userId(response.data.userId);
        }
        return response;
    }

    async onLeavePostalCodeOrHouseNumberField() {
        if (this.postalCode() && this.postalCode().length > 0
            && this.houseNumber() && this.houseNumber().length > 0) {
            const result = await lookupAddress(this.postalCode(), this.houseNumber());

            if (result.success) {
                this.address(result.street);
                this.city(result.town);
            }
        }
    }
}

class VerificationModel {
    emailVerified: KnockoutObservable<boolean>;
    adminVerified: KnockoutObservable<boolean>;

    constructor(model: any = {}) {
        this.emailVerified = ko.observable<boolean>(model.emailVerified || false);
        this.adminVerified = ko.observable<boolean>(model.adminVerified);
    }

    async sendEmailCode(): Promise<CustomResponse> {
        var fd = new FormData();

        const response = await fetch('/Account/SendEmailVerificationCode', {
            method: 'POST',
            body: fd
        });

        return response;
    }
}

const pageModel = new RegistrationModel(model || {});
ko.applyBindings(pageModel);
